import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import useBackgroundChecks from "hooks/olreadia/useBackgroundChecks";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";

const { Text } = Typography;

const BACKGROUND_CHECK_TYPES = [
  { value: "Profile validation and verification", label: "Profile validation and verification" },
  { value: "Criminal record checks", label: "Criminal record checks" },
];

const CHECK_STATUS_COLORS = {
  REQUESTED: "gold",
  PENDING: "cyan",
  IN_PROGRESS: "blue",
  PASSED: "green",
  FAILED: "red",
  CANCELED: "default",
};

const toLabel = (value) =>
  value ? String(value).replaceAll("_", " ").replace(/\b\w/g, (match) => match.toUpperCase()) : "-";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : String(value);
};

export default function BackgroundChecksPanel({
  profile,
  helperProfile,
  professionProfileId,
  userId,
  profileName,
  sectorLabel,
  createDrawerOpen,
  onCreateDrawerOpenChange,
  onReloadProfile,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";
  const { checks, total, loading, error, search, createForProfessionProfile } = useBackgroundChecks();

  const [createForm] = Form.useForm();
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState();
  const [typeFilter, setTypeFilter] = useState();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);

  const activeFilterCount = [searchTerm.trim(), statusFilter, typeFilter].filter(Boolean).length;

  useEffect(() => {
    const timeout = setTimeout(() => {
      search({
        ...(professionProfileId ? { professionProfileId } : {}),
        page,
        size: pageSize,
        ...(searchTerm.trim() ? { q: searchTerm.trim() } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(typeFilter ? { checkType: typeFilter } : {}),
      });
    }, searchTerm ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [professionProfileId, page, pageSize, search, searchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    setPage(0);
  }, [professionProfileId, searchTerm, statusFilter, typeFilter]);

  const handleCreateCheck = async () => {
    if (!professionProfileId) return;
    setCreateSubmitting(true);
    try {
      const values = await createForm.validateFields();
      await createForProfessionProfile(
        professionProfileId,
        {
          checkType: values.checkType,
          notes: values.notes || null,
        },
        actorId,
      );
      onCreateDrawerOpenChange?.(false);
      createForm.resetFields();
      onReloadProfile?.();
      await search({ professionProfileId, page: 0, size: pageSize });
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Failed to create background check");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openCheckDetails = (record) => {
    navigate(paths.helperProfessionBackgroundCheckDetails(record.id), {
      state: {
        check: record,
        returnTo: paths.helperProfessionBackgroundChecks,
        returnState: {
          profile: profile || null,
          helperProfile: helperProfile || null,
        },
        display: {
          name:
            helperProfile?.fullName ||
            [helperProfile?.firstName, helperProfile?.middleName, helperProfile?.lastName]
              .filter(Boolean)
              .join(" ") ||
            helperProfile?.userFullName ||
            record?.userFullName ||
            record?.userName ||
            record?.fullName ||
            profile?.userFullName ||
            profile?.userName ||
            profile?.fullName ||
            "-",
          email:
            helperProfile?.email ||
            helperProfile?.userEmail ||
            record?.userEmail ||
            record?.email ||
            profile?.userEmail ||
            profile?.email ||
            "-",
          phone:
            helperProfile?.phone ||
            helperProfile?.userPhone ||
            record?.userPhone ||
            record?.phone ||
            profile?.userPhone ||
            profile?.phone ||
            "-",
          photo:
            helperProfile?.profilePhoto ||
            helperProfile?.photo ||
            record?.profilePhoto ||
            record?.photo ||
            profile?.profilePhoto ||
            profile?.photo ||
            null,
        },
        profile: profile || {
          id: record?.professionProfileId || professionProfileId,
          userId: record?.userId || userId,
          profileName: record?.profileName || profileName,
          helperProfile,
          sector: sectorLabel ? { name: sectorLabel } : undefined,
        },
      },
    });
  };

  const columns = [
    {
      title: "Professional",
      dataIndex: "userFullName",
      key: "userFullName",
      width: 220,
      render: (value) => (
        <Tooltip title={value || "Unnamed user"}>
          <Text strong ellipsis style={{ maxWidth: 195 }}>
            {value || "Unnamed user"}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Check",
      key: "checkType",
      width: 320,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Tooltip title={record?.checkType || "No check type"}>
            <Text strong ellipsis style={{ maxWidth: 290 }}>
              {record?.checkType || "No check type"}
            </Text>
          </Tooltip>
          <Tooltip title={record?.notes || "No notes"}>
            <Text type="secondary" ellipsis style={{ maxWidth: 290, fontSize: 12 }}>
              {record?.notes || "No notes"}
            </Text>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 125,
      render: (value) => (
        <Tag color={CHECK_STATUS_COLORS[value] || "default"} bordered={false}>
          {value === "PASSED" ? "Reviewed" : value === "FAILED" ? "Flagged" : toLabel(value)}
        </Tag>
      ),
    },
    {
      title: "Requested",
      dataIndex: "requestedAt",
      key: "requestedAt",
      width: 150,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Updated",
      dataIndex: "completedAt",
      key: "completedAt",
      width: 150,
      render: (value, record) => formatDateTime(value || record?.updatedAt || record?.lastUpdatedAt),
    },
  ];

  return (
    <Card style={{ borderRadius: 14, border: "none", boxShadow: "none" }} styles={{ body: { padding: 12 } }}>
      <Text type="secondary" style={{ display: "block", marginBottom: 10, fontSize: 13 }}>
        {total} matching · {activeFilterCount} filters active
      </Text>

      <Row gutter={[10, 10]} align="middle" style={{ marginBottom: 12 }}>
        <Col xs={24} md={10} lg={8}>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            placeholder="Search profile, notes, or check ID"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            style={{ borderRadius: 12 }}
          />
        </Col>
        <Col xs={12} md={7} lg={5}>
          <Select
            allowClear
            value={typeFilter}
            options={BACKGROUND_CHECK_TYPES}
            placeholder="All check types"
            onChange={setTypeFilter}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={12} md={7} lg={4}>
          <Select
            allowClear
            value={statusFilter}
            placeholder="All statuses"
            onChange={setStatusFilter}
            style={{ width: "100%" }}
            options={Object.keys(CHECK_STATUS_COLORS).map((value) => ({ value, label: toLabel(value) }))}
          />
        </Col>
        <Col xs={24} lg={7} style={{ display: "flex", justifyContent: "flex-end" }}>
          {profile ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onCreateDrawerOpenChange?.(true)}>
              Create
            </Button>
          ) : null}
        </Col>
      </Row>

      {error ? (
        <Card size="small" style={{ marginBottom: 12, borderRadius: 12, border: "none", boxShadow: "none" }}>
          <Text type="danger">{error}</Text>
        </Card>
      ) : null}

      <Table
        rowKey={(record) => String(record?.id)}
        loading={loading}
        columns={columns}
        dataSource={checks}
        onRow={(record) => ({
          onClick: () => openCheckDetails(record),
        })}
        pagination={{
          current: page + 1,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage - 1);
            setPageSize(nextPageSize);
          },
        }}
        tableLayout="fixed"
        scroll={{ x: "max-content" }}
        className="custom-table"
        size="small"
        locale={{
          emptyText: (
            <Empty
              description={professionProfileId
                ? "No background checks found for this profile."
                : "No background checks match the current filters."}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />

      <Drawer
        open={createDrawerOpen}
        title="Create background check"
        width={560}
        zIndex={1500}
        onClose={() => onCreateDrawerOpenChange?.(false)}
        destroyOnHidden
        styles={{ body: { paddingBottom: 96 } }}
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => onCreateDrawerOpenChange?.(false)}>Cancel</Button>
            <Button type="primary" onClick={handleCreateCheck} loading={createSubmitting}>
              Create
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card size="small" style={{ borderRadius: 12, border: "none", boxShadow: "none" }}>
            <Space direction="vertical" size={4}>
              <Text type="secondary">Profile</Text>
              <Text strong>{profileName}</Text>
              <Text type="secondary">{sectorLabel || "No sector provided"}</Text>
            </Space>
          </Card>
          <Form layout="vertical" form={createForm}>
            <Form.Item
              name="checkType"
              label="Check type"
              rules={[{ required: true, message: "Select a background check type" }]}
            >
              <Select options={BACKGROUND_CHECK_TYPES} placeholder="Select type" />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={4} placeholder="Optional notes for the check" />
            </Form.Item>
          </Form>
        </Space>
      </Drawer>
    </Card>
  );
}
